/** Module A — Curve Completion: tessellated polylines from control points (centripetal Catmull-Rom backbone + multi-octave fBm lateral noise on circles, per-CP envelope zeroes).
 *  Phase 7: V2 `tessellateIsoline` removed — V3 features flow through `tessellateBackboneOnly` (called by `collectRasterFeatures`); the fBm-noise path (`tessellateFbmNoise`) is kept for the generic non-terrain path mesh generator. */
import FastNoiseLite from "fastnoise-lite";
import {rngStream} from "./TerrainRNG.js";
import {Pt} from "./Poisson/@Shared/ConstraintRasterizer/RasterFeatures_Pure.js";

/** Coordinate space the fBm noise is sampled in — identical to the canonical `NoiseSpace` from `Server/DB/mapObjects/@MapObject/ObjPath.ts`, re-declared locally so this engine module doesn't pull Server/DB (→ MobX) into its import graph.
 *  The call site passes `cfg.noiseSpace` straight through; TS's structural union matching keeps the two identical. */
type NoiseSpace = "curve" | "local" | "world";

export interface TessellatedCurve {
	points: Pt[];
	/** Mean distance from centroid to control points. */
	meanRadius: number;
	/** Approximate total arc length. */
	curveLength: number;
}

// ---- Math helpers ----

function smoothstepSS(edge0: number, edge1: number, x: number): number {
	const t = Math.max(0, Math.min(1, (x - edge0) / (edge1 - edge0)));
	return t * t * (3 - 2 * t);
}

function ptDist(a: Pt, b: Pt): number {
	const dx = b.x - a.x, dy = b.y - a.y;
	return Math.sqrt(dx*dx + dy*dy);
}

/** Sample multi-octave periodic fBm noise at parametric `u` in a segment (segment's noise instances / phase / spatial freq `kAt`); returns ~[-1, 1].
 *  Hoisted to module scope so the per-step loop in `TessellateFbmNoise` doesn't allocate a closure per backbone step. */
function sampleFbmNoise(
	instances: FastNoiseLite[], phase: number, kAt: number, u: number,
	fbmOctaves: number, fbmPersistence: number,
): number {
	const angle = phase + 2 * Math.PI * (u * kAt);
	let noiseSum = 0;
	let ampSum = 0;
	let amp = 1.0;
	let k = 1.0;
	for (let oct = 0; oct < fbmOctaves; oct++) {
		const nx = Math.cos(angle) * k;
		const ny = Math.sin(angle) * k;
		noiseSum += (instances[oct].GetNoise(nx, ny) as number) * amp;
		ampSum += amp;
		amp *= fbmPersistence;
		k *= 2.0;
	}
	return noiseSum / ampSum; // normalize to ~[-1, 1]
}

// ---- Shared Catmull-Rom backbone math (2026-08-02 refactor) ----
// `InterpolateConfig` was flattened (CR is the only backbone), so engine + renderer share ONE evaluator here,
// mirroring three.js `CatmullRomCurve3.getPoint` to preserve the pre-flatten renderer shape bit-for-bit (was private in PathTransforms_Pure.ts).

/** Mirrors the `Curve_CatmullRom_Subtype` type from `Server/DB/.../ObjPath.ts`. Re-declared
 *  here for the same reason as `NoiseSpace` below: keep this engine module free of any
 *  Server/DB (and transitively MobX) import. */
type CatmullRomSubtype = "centripetal" | "chordal" | "catmullrom";

/** three.js `CatmullRomCurve3.getPoint` math inlined for 2D: centripetal (pow=0.25) / chordal (pow=0.5) / catmullrom (tension-weighted tangents).
 *  Mirrors the three.js reference impl so the pre-flatten renderer curve shape is preserved bit-for-bit. */
export function CatmullRomPoint(
	p0: {x: number; y: number}, p1: {x: number; y: number}, p2: {x: number; y: number}, p3: {x: number; y: number},
	t: number, subtype: CatmullRomSubtype = "centripetal", tension: number = 0.5,
): {x: number; y: number} {
	if (subtype === "centripetal" || subtype === "chordal") {
		const pow = subtype === "chordal" ? 0.5 : 0.25;
		const dx0 = p0.x - p1.x, dy0 = p0.y - p1.y;
		const dx1 = p1.x - p2.x, dy1 = p1.y - p2.y;
		const dx2 = p2.x - p3.x, dy2 = p2.y - p3.y;
		let dt0 = Math.pow(dx0 * dx0 + dy0 * dy0, pow);
		let dt1 = Math.pow(dx1 * dx1 + dy1 * dy1, pow);
		let dt2 = Math.pow(dx2 * dx2 + dy2 * dy2, pow);
		// Safety check for repeated points (three.js: < 1e-4 -> clamp to 1.0 / neighbour).
		if (dt1 < 1e-4) dt1 = 1.0;
		if (dt0 < 1e-4) dt0 = dt1;
		if (dt2 < 1e-4) dt2 = dt1;
		const x = nonuniformCatmullRom(p0.x, p1.x, p2.x, p3.x, dt0, dt1, dt2, t);
		const y = nonuniformCatmullRom(p0.y, p1.y, p2.y, p3.y, dt0, dt1, dt2, t);
		return {x, y};
	}
	// catmullrom: tension-weighted tangents.
	const x = uniformCatmullRom(p0.x, p1.x, p2.x, p3.x, tension, t);
	const y = uniformCatmullRom(p0.y, p1.y, p2.y, p3.y, tension, t);
	return {x, y};
}

/** four-point stencil for one control-point segment, with closed/open edge handling
 *  mirroring three.js getPoint. `seg` is the segment index (p1=cp[seg], p2=cp[seg+1]). */
function crStencil(
	cp: ReadonlyArray<{x: number; y: number}>, n: number, seg: number, closed: boolean,
): [{x: number; y: number}, {x: number; y: number}, {x: number; y: number}, {x: number; y: number}] {
	const p1 = cp[seg];
	const p2 = cp[(seg + 1) % n];
	let p0: {x: number; y: number}, p3: {x: number; y: number};
	if (closed || seg > 0) {
		p0 = cp[(seg - 1 + n) % n];
	} else {
		// Extrapolate first point: p0 = p1 + (p1 - p2). (the renderer-side open-curve edge.)
		p0 = {x: cp[0].x + (cp[0].x - cp[1].x), y: cp[0].y + (cp[0].y - cp[1].y)};
	}
	if (closed || seg + 2 < n) {
		p3 = cp[(seg + 2) % n];
	} else {
		// Extrapolate last point: p3 = p_last + (p_last - p_prev).
		p3 = {x: cp[n - 1].x + (cp[n - 1].x - cp[n - 2].x), y: cp[n - 1].y + (cp[n - 1].y - cp[n - 2].y)};
	}
	return [p0, p1, p2, p3];
}

/** three.js CubicPoly.initNonuniformCatmullRom + calc, inlined for one axis. */
function nonuniformCatmullRom(x0: number, x1: number, x2: number, x3: number, dt0: number, dt1: number, dt2: number, t: number): number {
	// compute tangents when parameterized in [t1,t2]
	let t1 = (x1 - x0) / dt0 - (x2 - x0) / (dt0 + dt1) + (x2 - x1) / dt1;
	let t2 = (x2 - x1) / dt1 - (x3 - x1) / (dt1 + dt2) + (x3 - x2) / dt2;
	// rescale tangents for parametrization in [0,1]
	t1 *= dt1;
	t2 *= dt1;
	// init(x1, x2, t1, t2): c0=x1, c1=t1, c2=-3*x1+3*x2-2*t1-t2, c3=2*x1-2*x2+t1+t2
	const c0 = x1, c1 = t1, c2 = -3 * x1 + 3 * x2 - 2 * t1 - t2, c3 = 2 * x1 - 2 * x2 + t1 + t2;
	const t2sq = t * t;
	return c0 + c1 * t + c2 * t2sq + c3 * t2sq * t;
}

/** three.js CubicPoly.initCatmullRom + calc, inlined for one axis. */
function uniformCatmullRom(x0: number, x1: number, x2: number, x3: number, tension: number, t: number): number {
	// init(x1, x2, tension*(x2-x0), tension*(x3-x1))
	const c0 = x1, c1 = tension * (x2 - x0), c2 = -3 * x1 + 3 * x2 - 2 * (tension * (x2 - x0)) - (tension * (x3 - x1)), c3 = 2 * x1 - 2 * x2 + (tension * (x2 - x0)) + (tension * (x3 - x1));
	const t2sq = t * t;
	return c0 + c1 * t + c2 * t2sq + c3 * t2sq * t;
}

// ---- Main tessellation (backbone builder) ----

/** Dense per-segment sample count capturing the Catmull-Rom shape before arc-length re-sampling; must be high enough that integration stays accurate on short segments (24m × 64 samples = 0.375m).
 *  Used only by the arc-length-uniform branch. */
const BACKBONE_DENSE_SAMPLES_PER_SEGMENT = 64;

/** Backbone tessellation options (CR params follow InterpolateConfig's flattened fields). Exactly one drives sample density:
 *  - `metersPerOutputSegment` (> 0): arc-length-uniform re-sampling → constant world-distance baseline → no V-at-CP artefact when fBm is enabled; takes precedence when both are set.
 *  - `outputSegmentRatio` (≥1): fixed points-per-CP-segment (legacy renderer behaviour). */
export interface BackboneOptions {
	subtype?: CatmullRomSubtype;
	tension?: number;
	/** When ≥1, use fixed-points-per-segment. Ignored when `metersPerOutputSegment` > 0. */
	outputSegmentRatio?: number;
	/** When > 0, re-sample at uniform arc-length steps. Takes precedence over `outputSegmentRatio`. */
	metersPerOutputSegment?: number;
}

/** Internal result of the backbone builder: the tessellated points + per-point segment index
 *  and uInSeg (used by `TessellateFbmNoise`'s noise envelope / per-segment noise), plus the
 *  total arc length. */
interface BackboneResult {
	points: Pt[];
	segIdxAt: number[];
	uInSegAt: number[];
	totalLength: number;
}

/** Build a Catmull-Rom backbone from control points (shared subtype/tension-aware evaluator): arc-length-uniform when `metersPerOutputSegment > 0` (takes precedence), else fixed-points-per-segment when `outputSegmentRatio ≥ 1`, else `outputSegmentRatio = 10`; <2 points returned as-is.
 *  Single builder shared by the plain-CR (`TessellateBackbone`) and fBm-noise (`TessellateFbmNoise`, displacing on top) interpolators; replaces `BuildArcLengthUniformBackbone` + renderer `ApplyCatmullRomInterpolate` (CR math now `CatmullRomPoint`). */
export function BuildCatmullRomBackbone(
	cp: ReadonlyArray<{x: number; y: number}>,
	closed: boolean,
	opts: BackboneOptions = {},
): BackboneResult {
	const n = cp.length;
	const subtype = opts.subtype ?? "centripetal";
	const tension = opts.tension ?? 0.5;
	if (n < 2) {
		return {points: cp.map(p=>({x: p.x, y: p.y})), segIdxAt: [], uInSegAt: [], totalLength: 0};
	}

	// `metersPerOutputSegment` (> 0) takes precedence when set; else fixed points per segment
	// (legacy renderer behaviour) when `outputSegmentRatio` ≥ 1; else default 10/segment.
	const mpsRaw = opts.metersPerOutputSegment;
	const ppsRaw = opts.outputSegmentRatio;
	const stepMetres = (mpsRaw != null && mpsRaw > 0) ? mpsRaw : 0;
	if (stepMetres > 0) {
		// ── Branch B: arc-length-uniform re-sampling (constant world-distance baseline → no V-at-CP artefact when fBm is enabled) ──
		const densePerSeg = BACKBONE_DENSE_SAMPLES_PER_SEGMENT;
		// Open paths must NOT build a closing segment (crStencil for i = n-1 would wrap p2 back to
		// cp[0], fabricating a return-to-start segment that forces the path closed). Mirror Branch A's
		// segmentCount so an open path tessellates n-1 segments and stays a polyline.
		const segmentCount = closed ? n : n - 1;
		const totalDense = segmentCount * densePerSeg;
		const denseBackbone: Pt[] = new Array(totalDense);
		const denseSegIdx: number[] = new Array(totalDense);
		const denseUInSeg: number[] = new Array(totalDense);
		for (let i = 0; i < segmentCount; i++) {
			const [p0, p1, p2, p3] = crStencil(cp, n, i, closed);
			for (let s = 0; s < densePerSeg; s++) {
				const t = s / densePerSeg;
				denseBackbone[i * densePerSeg + s] = CatmullRomPoint(p0, p1, p2, p3, t, subtype, tension);
				denseSegIdx[i * densePerSeg + s] = i;
				denseUInSeg[i * densePerSeg + s] = t;
			}
		}

		// Cumulative arc length over the dense backbone.
		const cumLen: number[] = new Array(totalDense);
		let totalLength = 0;
		for (let i = 0; i < (closed ? totalDense : totalDense - 1); i++) {
			cumLen[i] = totalLength;
			totalLength += ptDist(denseBackbone[i], denseBackbone[(i + 1) % totalDense]);
		}
		if (!closed && totalDense > 0) cumLen[totalDense - 1] = totalLength;

		// Re-sample at uniform arc-length intervals of `stepMetres`.
		const numSteps = Math.max(2, Math.ceil(totalLength / stepMetres));
		const stepLen = totalLength / numSteps;
		const points: Pt[] = new Array(numSteps);
		const segIdxAt: number[] = new Array(numSteps);
		const uInSegAt: number[] = new Array(numSteps);
		let denseIdx = 0;
		for (let i = 0; i < numSteps; i++) {
			const targetLen = i * stepLen;
			// Advance denseIdx until cumLen[denseIdx] is at or past targetLen.
			while (denseIdx < totalDense - 1 && cumLen[denseIdx + 1] < targetLen) denseIdx++;
			// Interpolate between denseBackbone[denseIdx] and denseBackbone[denseIdx+1].
			const lo = cumLen[denseIdx];
			const hi = cumLen[(denseIdx + 1) % totalDense];
			const span = hi - lo;
			const localT = span > 1e-9 ? (targetLen - lo) / span : 0;
			const p0 = denseBackbone[denseIdx];
			const p1 = denseBackbone[(denseIdx + 1) % totalDense];
			points[i] = {
				x: p0.x + (p1.x - p0.x) * localT,
				y: p0.y + (p1.y - p0.y) * localT,
			};
			// Interpolate segIdx/uInSeg too (uInSeg via the dense sample's value, linearly).
			segIdxAt[i] = denseSegIdx[denseIdx];
			const u0 = denseUInSeg[denseIdx];
			const u1 = denseUInSeg[(denseIdx + 1) % totalDense];
			// Handle the wrap at dense-segment boundary (u goes 0.98→0.0 of the NEXT seg).
			const seg0 = denseSegIdx[denseIdx];
			const seg1 = denseSegIdx[(denseIdx + 1) % totalDense];
			uInSegAt[i] = seg0 === seg1
				? u0 + (u1 - u0) * localT
				: (localT < 0.5 ? u0 : u1); // cross-segment: pick whichever is closer
		}

		// For closed curves, the last step wraps to the first; adjust totalLength to match.
		// For open curves, the last step is the endpoint.
		if (!closed && numSteps > 0) {
			// Snap the last point to the exact endpoint.
			points[numSteps - 1] = denseBackbone[totalDense - 1];
			segIdxAt[numSteps - 1] = n - 1;
			uInSegAt[numSteps - 1] = 1;
		}

		return {points, segIdxAt, uInSegAt, totalLength};
	}

	// ── Branch A: fixed points per segment (legacy renderer behaviour) ──
	const pps = (ppsRaw != null && ppsRaw >= 1) ? Math.max(1, Math.floor(ppsRaw)) : 0;
	if (pps > 0) {
		const segmentCount = closed ? n : n - 1;
		const points: Pt[] = new Array(segmentCount * pps);
		const segIdxAt: number[] = new Array(segmentCount * pps);
		const uInSegAt: number[] = new Array(segmentCount * pps);
		// Total arc length via the same dense integration the uniform branch uses, so
		// `curveLength` reported to the caller stays consistent across branches.
		let totalLength = 0;
		let prevPt: Pt | n = null;
		for (let seg = 0; seg < segmentCount; seg++) {
			const [p0, p1, p2, p3] = crStencil(cp, n, seg, closed);
			for (let s = 0; s < pps; s++) {
				const t = s / pps;
				const pt = CatmullRomPoint(p0, p1, p2, p3, t, subtype, tension);
				const idx = seg * pps + s;
				points[idx] = pt;
				segIdxAt[idx] = seg;
				uInSegAt[idx] = t;
				if (prevPt) totalLength += ptDist(prevPt, pt);
				prevPt = pt;
			}
		}
		// Close the loop for closed curves (last → first): the first point of segment 0 at
		// t=0 IS cp[0], and the last sampled t<1 — add the closing arc-to-first to totalLength.
		if (closed && points.length > 0) totalLength += ptDist(points[points.length - 1], points[0]);
		return {points, segIdxAt, uInSegAt, totalLength};
	}

	// Neither field set: fall back to the default fixed-pps branch (10 per segment).
	return BuildCatmullRomBackbone(cp, closed, {...opts, outputSegmentRatio: 10, metersPerOutputSegment: undefined});
}

/** Tessellate a generic closed-or-open curve: centripetal Catmull-Rom backbone + multi-octave fBm lateral noise (isolines + non-isoline paths). */
export function TessellateFbmNoise(
	controlPoints: ReadonlyArray<{x: number; y: number}>,
	closed: boolean,
	shapeWavelength: number,
	shapeRoughness: number,
	fbmOctaves: number,
	fbmPersistence: number,
	makeShapeRng: ()=>()=>number,
	controlPointBlendBand: number = 0.2,
	noiseSpace: NoiseSpace = "local",
	objWorldPos: {x: number, y: number}|n = null,
	/** Catmull-Rom backbone options. When unset entirely, the legacy 2m arc-length-uniform
	 *  centripetal backbone is used (the pre-2026-08-02 `BuildArcLengthUniformBackbone` shape). */
	backboneOpts: BackboneOptions = {metersPerOutputSegment: 2},
): TessellatedCurve {
	const cp = controlPoints;
	const n = cp.length;
	if (n < 3) {
		const pts = cp.map(p=>({x: p.x, y: p.y}));
		let cx2 = 0, cy2 = 0;
		for (const p of cp) { cx2 += p.x; cy2 += p.y; }
		cx2 /= Math.max(n, 1); cy2 /= Math.max(n, 1);
		let mr2 = 0;
		for (const p of cp) mr2 += Math.sqrt((p.x-cx2)**2 + (p.y-cy2)**2);
		return {points: pts, meanRadius: mr2 / Math.max(n, 1), curveLength: 0};
	}

	// Step 1: Catmull-Rom backbone via the shared `BuildCatmullRomBackbone` (honours all options). With
	// `metersPerOutputSegment` active, steps are ~that many metres apart → constant prev/next normal
	// baseline everywhere — no V-shape at CP boundaries.
	const {points: backbone, segIdxAt, uInSegAt, totalLength} = BuildCatmullRomBackbone(cp, closed, backboneOpts);
	const totalSteps = backbone.length;

	// Centroid + mean radius (from control points)
	let cx = 0, cy = 0;
	for (const p of cp) { cx += p.x; cy += p.y; }
	cx /= n; cy /= n;
	let meanRadius = 0;
	for (const p of cp) meanRadius += Math.sqrt((p.x-cx)**2 + (p.y-cy)**2);
	meanRadius /= n;

	// fBm shape parameters. Wavelength fallback: mean CP spacing (geometric straight-line), NOT
	// totalLength/n (mean segment arc length — close, but the historical fallback used straight-line
	// CP spacing); either keeps the pre-Option-B reference look on uniform-spacing polygons.
	let meanCPSpacing = 0;
	for (let i = 0; i < n; i++) {
		meanCPSpacing += ptDist(cp[i], cp[(i + 1) % n]);
	}
	meanCPSpacing /= closed ? n : (n - 1);
	const wl = shapeWavelength > 0 ? shapeWavelength : meanCPSpacing;

	// Amplitude cap keyed to mean CP spacing (unchanged from pre-Option-B).
	/*const roughnessAmp = Math.min(
		shapeRoughness * meanRadius,
		0.25 * meanCPSpacing,
	);*/
	const roughnessAmp = shapeRoughness * meanRadius;

	// ---- noise field setup ----
	// "curve": per-segment independent noise (B+B+1), optional cross-fade blend at CPs.
	// "local"/"world": ONE continuous 2D field at backbone point coords — envelope/normal/offset shared; only `noiseVal` differs.
	if (noiseSpace !== "curve") {
		// ---- Option C: continuous 2D noise field (local or world) ----
		// One noise-instance set for the whole curve; each step samples at its (x,y)/`wl` (+ world offset in "world" mode).
		// C∞-continuous → no V at CPs / CP-clustering / collinear-CP bumpiness; bump density ~1/wl per metre, CP-density-independent.
		const offX = noiseSpace === "world" && objWorldPos ? objWorldPos.x : 0;
		const offY = noiseSpace === "world" && objWorldPos ? objWorldPos.y : 0;
		const noiseInstances: FastNoiseLite[] = new Array(fbmOctaves);
		for (let oct = 0; oct < fbmOctaves; oct++) {
			const rng = makeShapeRng();
			const seed = Math.floor(rng() * 2147483647);
			const fnl = new FastNoiseLite(seed);
			fnl.SetNoiseType(FastNoiseLite.NoiseType.OpenSimplex2);
			fnl.SetFrequency(1.0 / Math.max(wl, 1)); // 1/wl so one noise cycle = wl metres
			noiseInstances[oct] = fnl;
		}

		// Fixed-world-distance envelope: offset zero within ENV_ZERO of each CP, ramp to full over ENV_RAMP (scaled to
		// wavelength, capped to a fraction of meanCPSpacing). Unlike the old uInSeg-fractional envelope (15% of each segment's
		// length → 3.6m on a 24m seg but 78m on a 524m seg), tent-width is uniform at every CP → no V-at-CP, no over-suppression.
		const ENV_ZERO = Math.min(wl * 0.25, meanCPSpacing * 0.5);
		const ENV_RAMP = wl * 0.5;
		// Build the arc-length position of each CP boundary from the uniform backbone.
		// CP i is at the start of segment i (uInSeg=0). Find the backbone index where each
		// segment starts by tracking segIdxAt transitions.
		const cpArcLen: number[] = new Array(n);
		let cumArc = 0;
		cpArcLen[0] = 0;
		let lastSeg = segIdxAt[0];
		for (let i = 1; i < totalSteps; i++) {
			cumArc += ptDist(backbone[i - 1], backbone[i]);
			if (segIdxAt[i] !== lastSeg) {
				cpArcLen[segIdxAt[i]] = cumArc;
				lastSeg = segIdxAt[i];
			}
		}
		// For closed curves, the last CP (n-1's end = CP0's start) wraps to totalLength.
		// For open curves, the last CP is the endpoint; set its arc-length so the envelope
		// tapers to zero at the path's far end (its segment is the final one, n-2).
		if (!closed) cpArcLen[n - 1] = totalLength;
		// Build a sorted array of CP arc-length positions for the nearest-CP lookup.
		const stepLen = totalLength / totalSteps;

		const result: Pt[] = new Array(totalSteps);
		for (let i = 0; i < totalSteps; i++) {
			const pt = backbone[i];
			const arcPos = i * stepLen;

			// Distance to the nearest CP in arc-length (wrapping for closed curves).
			let minCPDist = Infinity;
			for (let c = 0; c < n; c++) {
				let d = Math.abs(arcPos - cpArcLen[c]);
				if (closed) d = Math.min(d, totalLength - d); // wrap
				if (d < minCPDist) minCPDist = d;
			}
			const env = smoothstepSS(ENV_ZERO, ENV_ZERO + ENV_RAMP, minCPDist);

			// Multi-octave fBm sampled at the backbone point's (offset) coords: octave `oct` doubles frequency
			// via `(1 << oct)` spatial scale and decays amplitude by `persistence`; base freq 1/wl (set on the
			// FastNoiseLite instance) → coarsest octave has wavelength `wl` metres, octave `oct` has `wl / 2^oct`.
			const sx = (pt.x + offX);
			const sy = (pt.y + offY);
			let noiseSum = 0, ampSum = 0, amp = 1.0;
			for (let oct = 0; oct < fbmOctaves; oct++) {
				const freq = 1 << oct; // 1, 2, 4, 8, …
				noiseSum += (noiseInstances[oct].GetNoise(sx * freq, sy * freq) as number) * amp;
				ampSum += amp;
				amp *= fbmPersistence;
			}
			const noiseVal = noiseSum / ampSum;

			// Normal (perpendicular to tangent)
			const prev = backbone[(i - 1 + totalSteps) % totalSteps];
			const next = backbone[(i + 1) % totalSteps];
			const tx = next.x - prev.x, ty = next.y - prev.y;
			const tlen = Math.sqrt(tx*tx + ty*ty) || 1;
			const nx = -ty / tlen, ny = tx / tlen;

			const offset = env * roughnessAmp * noiseVal;
			result[i] = {x: pt.x + nx * offset, y: pt.y + ny * offset};
		}
		return {points: result, meanRadius, curveLength: totalLength};
	}

	// ---- "curve" mode: per-segment fBm (Option B) + B+1 cross-fade ----
	// Each CP segment owns its noise: spatial frequency k_i = segArcLen_i/(2π·wl) → uniform ~1/wl bump density regardless of segment length, plus an octave instance seeded once per segment.
	// Phase = uInSeg·k_i with cross-segment offset i·2π (wraps once per CP segment); CP discontinuities masked by the env envelope zeroing.

	// Per-segment arc length from the uniform backbone's per-step segment indices: sum the
	// inter-step distances for steps whose segIdxAt == seg.
	const segArcLen: number[] = new Array(n).fill(0);
	for (let i = 0; i < (closed ? totalSteps : totalSteps - 1); i++) {
		const seg = segIdxAt[i];
		segArcLen[seg] += ptDist(backbone[i], backbone[(i + 1) % totalSteps]);
	}

	const segNoiseInstances: FastNoiseLite[][] = new Array(n);
	const segNoiseK: number[] = new Array(n);
	for (let seg = 0; seg < n; seg++) {
		const segLen = segArcLen[seg];
		const kSeg = segLen / (2 * Math.PI * Math.max(wl, 1));
		segNoiseK[seg] = kSeg;
		const arr: FastNoiseLite[] = new Array(fbmOctaves);
		for (let oct = 0; oct < fbmOctaves; oct++) {
			const rng = makeShapeRng();
			const seed = Math.floor(rng() * 2147483647);
			const fnl = new FastNoiseLite(seed);
			fnl.SetNoiseType(FastNoiseLite.NoiseType.OpenSimplex2);
			fnl.SetFrequency(1.0);
			arr[oct] = fnl;
		}
		segNoiseInstances[seg] = arr;
	}

	// B+1 cross-fade: in the head band `[0, blendBand)` of each segment, the noise blends from
	// the previous segment's tail realization into this segment's head. C0-continuous noise
	// value across CPs (the derivative kink remains — see the V-at-CP caveat in the docstring).
	const blendActive = controlPointBlendBand > 0;
	const blendBand = blendActive ? Math.min(controlPointBlendBand, 0.5) : 0;

	const result: Pt[] = new Array(totalSteps);
	for (let i = 0; i < totalSteps; i++) {
		const seg = segIdxAt[i];
		const uInSeg = uInSegAt[i];
		const pt = backbone[i];
		const noiseInstances = segNoiseInstances[seg];
		const kSeg = segNoiseK[seg];
		const segPhase = seg * 2 * Math.PI;

		const prevSeg = (seg - 1 + n) % n;
		const hasPrev = closed || seg > 0;
		const prevNoiseInstances = hasPrev ? segNoiseInstances[prevSeg] : null;
		const prevKSeg = hasPrev ? segNoiseK[prevSeg] : 0;
		const prevSegPhase = hasPrev ? (prevSeg * 2 * Math.PI) : 0;

		const env = closed
			? smoothstepSS(0, 0.15, uInSeg) * smoothstepSS(1, 0.85, uInSeg)
			: smoothstepSS(0, 0.15, uInSeg) * smoothstepSS(1, 0.85, uInSeg);

		let noiseVal: number;
		if (blendActive && hasPrev && uInSeg < blendBand) {
			const w = smoothstepSS(blendBand, 0, uInSeg);
			const thisNoise = sampleFbmNoise(noiseInstances, segPhase, kSeg, uInSeg, fbmOctaves, fbmPersistence);
			const prevU = 1 + uInSeg;
			const prevN = sampleFbmNoise(prevNoiseInstances!, prevSegPhase, prevKSeg, prevU, fbmOctaves, fbmPersistence);
			noiseVal = w * prevN + (1 - w) * thisNoise;
		} else {
			noiseVal = sampleFbmNoise(noiseInstances, segPhase, kSeg, uInSeg, fbmOctaves, fbmPersistence);
		}

		const prev = backbone[(i - 1 + totalSteps) % totalSteps];
		const next = backbone[(i + 1) % totalSteps];
		const tx = next.x - prev.x, ty = next.y - prev.y;
		const tlen = Math.sqrt(tx*tx + ty*ty) || 1;
		const nx = -ty / tlen, ny = tx / tlen;

		const offset = env * roughnessAmp * noiseVal;
		result[i] = {x: pt.x + nx * offset, y: pt.y + ny * offset};
	}

	return {points: result, meanRadius, curveLength: totalLength};
}

/** Tessellate an open polyline with a plain centripetal Catmull-Rom backbone (no fBm) — V3's standard path-feature tessellator; the `collectRasterFeatures` bridge (Module G) converts ObjPath.points → solver cells (`closedIsoline` / `openDirichlet` / `cliff`).
 *  2m arc-length-uniform centripetal @ 0.5 (pre-2026-08-02 shape via shared `BuildCatmullRomBackbone`); kept centripetal-only for now — a future task may plumb `subtype`/`tension`/`outputSegmentRatio` from `InterpolateConfig` too. */
export function TessellateBackboneOnly(
	controlPoints: ReadonlyArray<{x: number; y: number}>,
	closed: boolean,
): TessellatedCurve {
	const cp = controlPoints;
	const n = cp.length;
	if (n < 3) {
		const pts = cp.map(p=>({x: p.x, y: p.y}));
		let cx2 = 0, cy2 = 0;
		for (const p of cp) { cx2 += p.x; cy2 += p.y; }
		cx2 /= Math.max(n, 1); cy2 /= Math.max(n, 1);
		let mr2 = 0;
		for (const p of cp) mr2 += Math.sqrt((p.x-cx2)**2 + (p.y-cy2)**2);
		return {points: pts, meanRadius: mr2 / Math.max(n, 1), curveLength: 0};
	}

	// Arc-length-uniform Catmull-Rom backbone (the legacy 2m step keeps prev/next normal
	// baseline uniform — no V-shapes at CP boundaries between short and long segments).
	const {points: backbone, totalLength} = BuildCatmullRomBackbone(cp, closed, {metersPerOutputSegment: 2});

	// Centroid + mean radius (from control points)
	let cx = 0, cy = 0;
	for (const p of cp) { cx += p.x; cy += p.y; }
	cx /= n; cy /= n;
	let meanRadius = 0;
	for (const p of cp) meanRadius += Math.sqrt((p.x-cx)**2 + (p.y-cy)**2);
	meanRadius /= n;

	return {points: backbone, meanRadius, curveLength: totalLength};
}
